import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Toaster } from "sonner";
import "./index.css";
import i18n from "@/app/i18n";
import { modules } from "@/app/modules";
import { makeRouter } from "@/app/router";
import { ApiError } from "@/shared/api/client";
import { isApex } from "@/shared/lib/host";
import { MaintenanceGate } from "@/shared/ui/Maintenance";

// The apex domain (no tenant) only serves signup and the workspace finder.
const ApexApp = lazy(() => modules.auth.pages().then((m) => ({ default: m.ApexApp })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
  queryCache: new QueryCache({ onError: (err) => handleAuthError(err) }),
  mutationCache: new MutationCache({ onError: (err) => handleAuthError(err) }),
});

const router = makeRouter(queryClient);

// Session ended (logout elsewhere, expiry) or 2FA enrollment became mandatory.
function handleAuthError(err: unknown) {
  if (!(err instanceof ApiError)) return;
  const path = window.location.pathname;
  const publicPage = ["/login", "/forgot-password", "/reset-password", "/accept-invite", "/auth/handoff", "/verify-email"].includes(path);
  if (err.status === 401 && !publicPage) {
    queryClient.clear();
    void router.navigate({ to: "/login", search: { next: path } as never });
  }
  if (err.code === "auth.totp_setup_required" && path !== "/settings/security") void router.navigate({ to: "/settings/security" });
  // Refused for a permission: the user's role may have changed since the page loaded (another
  // admin changed it), so their menu catches up with what they may now open.
  if (err.code === "permission.denied" && Date.now() - permissionsChecked > 10_000) {
    permissionsChecked = Date.now();
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  }
}
let permissionsChecked = 0;

// The installable app (manifest.webmanifest, sw.js): on workspaces only, and not in development.
if (import.meta.env.PROD && "serviceWorker" in navigator && !isApex()) {
  window.addEventListener("load", () => void navigator.serviceWorker.register("/sw.js"));
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MaintenanceGate>{isApex() ? <Suspense><ApexApp /></Suspense> : <RouterProvider router={router} />}</MaintenanceGate>
      {/* Toasts rise above the phone's bottom bar. */}
      <Toaster position="bottom-center" dir={i18n.dir()} toastOptions={{ style: { fontFamily: "inherit" } }}
        offset={{ bottom: "calc(var(--bottom-bar) + 24px)" }} mobileOffset={{ bottom: "calc(var(--bottom-bar) + 16px)" }} />
    </QueryClientProvider>
  </StrictMode>,
);
